import { MailOutlined } from '@ant-design/icons';
import { type IAdminProfile } from '@mahawi/eshop-common';
import { Card, Descriptions, Empty, Skeleton, Space } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { profileLoad } from '../../reducers/user/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import DetailPageHeader from '../detail-page-header';
import RelativeTime from '../relative-time';

function AdminProfile({
  accountUUID,
}: {
  accountUUID: string;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const profile: IAdminProfile | null = useSelector(
    (s: RootState) => s.User.profile,
  );
  const profileInProcess = useSelector(
    (s: RootState) => s.User.profileInProcess,
  );

  useEffect(() => {
    dispatch(profileLoad(accountUUID));
  }, [dispatch, accountUUID]);

  const isCurrentProfile: boolean = profile?.uuid === accountUUID;

  if (!isCurrentProfile && profileInProcess) {
    return <Skeleton active />;
  }

  if (!profile || !isCurrentProfile) {
    return <Empty description="Staff account not found" />;
  }

  return (
    <>
      <DetailPageHeader
        title={profile.username ?? profile.email}
        onReload={(): void => {
          dispatch(profileLoad(accountUUID));
        }}
        isReloadDisabled={profileInProcess}
        uuid={profile.uuid}
      />

      <Card size="small" title="Staff profile" style={{ maxWidth: 640 }}>
        <Descriptions
          bordered
          size="small"
          column={1}
          items={[
            {
              key: 'username',
              label: 'Username',
              children: profile.username ?? EMPTY_VALUE,
            },
            {
              key: 'email',
              label: 'Email',
              children: (
                <Space size={SPACING.xs}>
                  <a href={`mailto:${profile.email}`}>
                    <Space size={SPACING.xs}>
                      <MailOutlined />
                      {profile.email}
                    </Space>
                  </a>
                  <CopyToClipboard
                    text={profile.email}
                    size="small"
                    type="link"
                  />
                </Space>
              ),
            },
            {
              key: 'lastSeenAt',
              label: 'Last seen',
              children: profile.lastSeenAt ? (
                <RelativeTime value={profile.lastSeenAt} />
              ) : (
                EMPTY_VALUE
              ),
            },
            {
              key: 'activeSessionCount',
              label: 'Active sessions',
              children: profile.activeSessionCount,
            },
          ]}
        />
      </Card>
    </>
  );
}

export default AdminProfile;
